import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

// Color constants for dataset markers
const DATASET_COLORS = {
  DEFAULT: {
    fill: '#3B82F6',
    stroke: '#1E40AF'
  },
  CLICKED: {
    fill: '#A855F7',
    stroke: '#7C3AED'
  }
} as const;

// TypeScript interfaces for Google Maps Dataset Layer types
interface DatasetAttributes {
  globalid?: string;
  nhs_rest_stop?: string;
  municipality?: string;
  highway_route?: string;
  number_of_spots?: number;
  mile_post?: string;
  county?: string;
  state?: string;
  [key: string]: any; // Allow other properties
}

interface DatasetFeature {
  datasetAttributes?: DatasetAttributes;
}

interface FeatureStyleParams {
  feature: DatasetFeature;
}

interface FeatureClickEvent {
  features?: DatasetFeature[];
  latLng?: {
    lat: () => number;
    lng: () => number;
  };
}

interface DatasetLayerProps {
  datasetId: string;
  onClick?: (event: google.maps.MapMouseEvent) => void;
  minZoom?: number; // Only show dataset features at this zoom level or higher
  maxZoom?: number; // Hide dataset features above this zoom level
}

/**
 * Component to display a Google Maps dataset layer.
 * The dataset must be associated with the map's style in Google Cloud Console.
 */
const DatasetLayer: React.FC<DatasetLayerProps> = ({ datasetId, onClick, minZoom = 10, maxZoom = 22 }) => {
  const map = useMap();
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const lastClickedFeatureIdRef = useRef<string | null>(null);
  const lastHoveredFeatureIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!map || !datasetId) return;

    // Create a feature layer for the dataset
    const featureLayer = (map as any).getDatasetFeatureLayer?.(datasetId);

    if (!featureLayer) {
      console.error(`Feature layer not found for dataset: ${datasetId}`);
      return;
    }

    // Create an InfoWindow instance
    if (!infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow();
    }

    // Define style presets
    const getDefaultStyle = (zoom: number) => {
      // Hide features if zoom is below minimum zoom level
      if (zoom < minZoom || zoom > maxZoom) {
        return { fillOpacity: 0, strokeOpacity: 0 };
      }

      return {
        strokeColor: DATASET_COLORS.DEFAULT.stroke,
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: DATASET_COLORS.DEFAULT.fill,
        fillOpacity: 0.4,
        pointRadius: 5,
      };
    };

    const getClickedStyle = (zoom: number) => {
      const baseStyle = getDefaultStyle(zoom);
      return {
        ...baseStyle,
        strokeColor: DATASET_COLORS.CLICKED.stroke,
        fillColor: DATASET_COLORS.CLICKED.fill,
        fillOpacity: 0.6,
        strokeWeight: 4,
      };
    };

    const getHoverStyle = (zoom: number) => {
      const baseStyle = getDefaultStyle(zoom);
      return {
        ...baseStyle,
        strokeWeight: (baseStyle.strokeWeight || 2) * 2, // Double the stroke weight on hover
        fillOpacity: 0.6,
      };
    };

    // Style the dataset features with interactive states
    const applyStyle = (params: FeatureStyleParams) => {
      const zoom = map.getZoom() || 10;
      const feature = params.feature;

      // Get a unique identifier for the feature
      const properties = feature?.datasetAttributes || {};
      const featureId = properties.globalid ||
        properties.nhs_rest_stop ||
        `${properties.municipality}_${properties.highway_route}`;

      // Apply clicked style if this feature is clicked
      if (lastClickedFeatureIdRef.current === featureId) {
        return getClickedStyle(zoom);
      }

      // Apply hover style if this feature is hovered
      if (lastHoveredFeatureIdRef.current === featureId) {
        return getHoverStyle(zoom);
      }

      // Apply default style
      return getDefaultStyle(zoom);
    };

    featureLayer.style = applyStyle;


    // Re-apply styles when zoom changes
    const zoomListener = map.addListener('zoom_changed', () => {
      featureLayer.style = applyStyle;
    });

    // Handle mouse move for hover effects
    const handleMouseMove = (event: FeatureClickEvent) => {
      const feature = event.features?.[0];
      if (!feature) {
        if (lastHoveredFeatureIdRef.current) {
          lastHoveredFeatureIdRef.current = null;
          featureLayer.style = applyStyle;
        }
        return;
      }

      const properties = feature.datasetAttributes || {};
      const featureId = properties.globalid ||
        properties.nhs_rest_stop ||
        `${properties.municipality}_${properties.highway_route}`;

      if (lastHoveredFeatureIdRef.current !== featureId) {
        lastHoveredFeatureIdRef.current = featureId;
        featureLayer.style = applyStyle;
      }
    };

    const mouseMoveListener = featureLayer.addListener('mousemove', handleMouseMove);

    // Handle feature clicks to display data
    const handleFeatureClick = (event: FeatureClickEvent) => {
      const feature = event.features?.[0];

      if (!feature) return;

      // Extract properties from the feature
      const properties = feature.datasetAttributes || {};

      // Get feature ID and update clicked state
      const featureId = properties.globalid ||
        properties.nhs_rest_stop ||
        `${properties.municipality}_${properties.highway_route}`;

      lastClickedFeatureIdRef.current = featureId;
      featureLayer.style = applyStyle;

      // Build HTML content for the InfoWindow with better styling
      let content = '<style>.gm-style-iw-c { padding: 0 !important; background: white !important; overflow: hidden !important; border-radius: 8px !important; } .gm-style-iw-ch { display: none !important; } .gm-style-iw button { display: none !important; } .gm-style-iw-d { overflow: hidden !important; padding: 0 !important; margin: 0 !important; } .gm-style-iw-d > div { margin: 0 !important; }</style>';
      content += '<div style="font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif; width: 280px; overflow: hidden; background: white; border-radius: 8px;">';

      // Header image with fixed aspect ratio - extends to edges
      content += '<img src="https://static.secur.space/app-images/truck-stop.jpeg" style="width: 100%; height: 140px; object-fit: cover; display: block;" alt="Rest Stop" />';

      // Content wrapper with padding
      content += '<div style="padding: 12px;">';

      // Title underneath image
      if (properties.nhs_rest_stop) {
        const title = properties.nhs_rest_stop;
        content += `<h3 style="margin: 0 0 12px 0; color: #1F2937; font-size: 18px; font-weight: bold; line-height: 1.4; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">${title}</h3>`;
      }

      // Details section with improved styling
      content += '<div style="display: flex; flex-direction: column; gap: 8px;">';

      if (properties.highway_route) {
        content += `<div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: #F3F4F6; border-radius: 6px; overflow: hidden;">`;
        content += `<span style="color: #6B7280; font-size: 13px; font-weight: 500; min-width: 80px; flex-shrink: 0;">Highway</span>`;
        content += `<span style="color: #1F2937; font-size: 14px; font-weight: 500; word-wrap: break-word; overflow-wrap: break-word;">${properties.highway_route}</span>`;
        content += `</div>`;
      }

      if (properties.mile_post) {
        content += `<div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: #F3F4F6; border-radius: 6px; overflow: hidden;">`;
        content += `<span style="color: #6B7280; font-size: 13px; font-weight: 500; min-width: 80px; flex-shrink: 0;">Mile Marker</span>`;
        content += `<span style="color: #1F2937; font-size: 14px; word-wrap: break-word; overflow-wrap: break-word;">Mile ${properties.mile_post}</span>`;
        content += `</div>`;
      }

      if (properties.municipality) {
        content += `<div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: #F3F4F6; border-radius: 6px; overflow: hidden;">`;
        content += `<span style="color: #6B7280; font-size: 13px; font-weight: 500; min-width: 80px; flex-shrink: 0;">Municipality</span>`;
        content += `<span style="color: #1F2937; font-size: 14px; word-wrap: break-word; overflow-wrap: break-word;">${properties.municipality}</span>`;
        content += `</div>`;
      }

      if (properties.county) {
        content += `<div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: #F3F4F6; border-radius: 6px; overflow: hidden;">`;
        content += `<span style="color: #6B7280; font-size: 13px; font-weight: 500; min-width: 80px; flex-shrink: 0;">County</span>`;
        content += `<span style="color: #1F2937; font-size: 14px; word-wrap: break-word; overflow-wrap: break-word;">${properties.county}</span>`;
        content += `</div>`;
      }

      if (properties.state) {
        content += `<div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: #F3F4F6; border-radius: 6px; overflow: hidden;">`;
        content += `<span style="color: #6B7280; font-size: 13px; font-weight: 500; min-width: 80px; flex-shrink: 0;">State</span>`;
        content += `<span style="color: #1F2937; font-size: 14px; word-wrap: break-word; overflow-wrap: break-word;">${properties.state}</span>`;
        content += `</div>`;
      }

      content += '</div>';

      // Get navigation button with lat/lng from event
      if (event.latLng) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

        content += `<a href="${googleMapsUrl}" target="_blank" rel="noopener noreferrer" style="display: block; margin-top: 12px; padding: 12px; background: #1E40AF; color: white; text-align: center; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">`;
        content += `<svg style="display: inline-block; margin-right: 8px; vertical-align: middle;" width="16" height="16" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">`;
        content += `<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>`;
        content += `</svg>`;
        content += `Get Directions`;
        content += `</a>`;
      }

      content += '</div>'; // Close padding wrapper
      content += '</div>'; // Close main wrapper

      // Position and display the InfoWindow
      infoWindowRef.current!.setContent(content);
      if (event.latLng) {
        infoWindowRef.current!.setPosition({
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        });
      }
      infoWindowRef.current!.setOptions({
        disableAutoPan: false,
        maxWidth: 280,
        pixelOffset: new google.maps.Size(0, -10),
      });
      infoWindowRef.current!.open({
        map,
        shouldFocus: false,
      });

      // Call custom onClick handler if provided
      if (onClick) {
        onClick(event as unknown as google.maps.MapMouseEvent);
      }
    };

    const clickListener = featureLayer.addListener('click', handleFeatureClick);

    // Close InfoWindow when clicking on the map (outside the InfoWindow)
    const mapClickListener = map.addListener('click', () => {
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      // Clear clicked state
      if (lastClickedFeatureIdRef.current) {
        lastClickedFeatureIdRef.current = null;
        featureLayer.style = applyStyle;
      }
    });

    return () => {
      google.maps.event.removeListener(clickListener);
      google.maps.event.removeListener(zoomListener);
      google.maps.event.removeListener(mapClickListener);
      google.maps.event.removeListener(mouseMoveListener);
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, [map, datasetId, onClick, minZoom, maxZoom]);

  // This component doesn't render anything itself
  return null;
};

export default DatasetLayer;
